"use client";

import { useMemo } from "react";
import { RecordTable } from "@/components/ui/record-table/record-table";
import {
  READ_ONLY_CAPABILITIES,
  defineRecordObject,
} from "@/components/ui/record-table/define-record-object";
import { useRecordTable } from "@/components/ui/record-table/use-record-table";
import type { RecordFieldKind } from "@/components/ui/record-table/types";
import type {
  AccessoryColumnDefinition,
  AccessoryProduct,
} from "@/lib/constants/accessory-columns";
import { buildAccessoryColumns } from "./build-accessory-columns";

interface AccessoryRecordTableProps {
  objectId: string;
  label: string;
  products: AccessoryProduct[];
  definitions: AccessoryColumnDefinition[];
  paginate?: boolean;
}

function fieldKind(
  definition: AccessoryColumnDefinition,
): RecordFieldKind {
  switch (definition.format) {
    case "price":
      return "currency";
    case "quantity":
      return "number";
    case "date":
      return "date";
    case undefined:
    case "boolean":
    case "dimensions":
    case "text":
      return "text";
    default: {
      const unhandled: never = definition.format;
      void unhandled;
      return "text";
    }
  }
}

function accessoryHref(product: AccessoryProduct): string {
  const slug = product.slug ?? product.publicIdentifier ?? product._id;
  return `/listing/${slug}`;
}

export function AccessoryRecordTable({
  objectId,
  label,
  products,
  definitions,
  paginate = false,
}: AccessoryRecordTableProps) {
  const object = useMemo(
    () =>
      defineRecordObject<AccessoryProduct>({
        id: objectId,
        label,
        fields: definitions.map((definition) => ({
          id: definition.id,
          label: definition.header,
          kind: fieldKind(definition),
          filterable: false,
          sortable: paginate && definition.sortable !== false,
        })),
        columns: buildAccessoryColumns(definitions, accessoryHref),
        defaultPageSize: 50,
        capabilities: READ_ONLY_CAPABILITIES,
        getRowId: (product) => product._id,
        getRowHref: accessoryHref,
      }),
    [definitions, label, objectId, paginate],
  );
  const state = useRecordTable(object);

  return (
    <RecordTable
      object={object}
      state={state}
      data={{ mode: paginate ? "client" : "none", rows: products }}
      emptyTitle={`No ${label.toLowerCase()} available`}
    />
  );
}
